/* =========================================================================
   GEAR UP MY CAR: QUICK ADD & VARIANT POPUP MODAL SCRIPT
   - 100% Real Shopify Server Data
   - Centered Screen Popup Modal Execution
   - Interactive Variant Selection & Live Pricing
   - AJAX Add-to-Cart with Drawer Integration
   ========================================================================= */

if (!customElements.get('quick-add-modal')) {
  const BaseModal = typeof ModalDialog !== 'undefined' ? ModalDialog : HTMLElement;

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends BaseModal {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification && this.openedBy) cartNotification.setActiveElement(this.openedBy);
        if (!this.modalContent || !this.modalContent.querySelector('.luxury-quick-add')) {
          this.modalContent.innerHTML = '';
        }

        if (preventFocus) this.openedBy = null;
        document.body.classList.remove('overflow-hidden');
        this.removeAttribute('open');
        if (typeof removeTrapFocus === 'function' && this.openedBy) removeTrapFocus(this.openedBy);
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      }

      show(opener) {
        this.openedBy = opener;

        // Ensure modal is directly inside document.body so no parent container can trap or clip it
        if (this.parentNode !== document.body) {
          document.body.appendChild(this);
        }

        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');

        if (this.modalContent && this.modalContent.querySelector('.luxury-quick-add')) {
          if (typeof trapFocus === 'function') {
            trapFocus(this, this.querySelector('[role="dialog"]') || this);
          }
          if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
          return;
        }

        if (!opener) return;
        opener.setAttribute('aria-disabled', 'true');
        opener.classList.add('loading');
        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');
        if (!productUrl) return;

        fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) {
              window.location.href = productUrl;
              return;
            }

            this.preprocessHTML(productElement);
            if (typeof HTMLUpdateUtility !== 'undefined') {
              HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            } else {
              this.modalContent.innerHTML = productElement.outerHTML;
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            if (typeof trapFocus === 'function') {
              trapFocus(this, this.querySelector('[role="dialog"]') || this);
            }
            if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
          })
          .catch(() => {
            window.location.href = productUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((dialog) => dialog.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        productElement.innerHTML = productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.section = `quickadd-${sectionId}`;
        });
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('li').forEach((listItem) => {
          listItem.setAttribute('role', 'presentation');
        });
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

/* =========================================================================
   GLOBAL MODAL-OPENER RESILIENT HANDLER
   Guarantees that clicking Choose options or Quick View reliably opens the popup
   ========================================================================= */
document.addEventListener('click', function(e) {
  // 1. Check if an opener was clicked
  const trigger = e.target.closest('modal-opener button, modal-opener, [data-modal]');
  if (trigger) {
    const openerEl = trigger.closest('modal-opener') || trigger;
    const targetSelector = openerEl.getAttribute('data-modal') || trigger.getAttribute('data-modal');
    if (targetSelector) {
      const modal = document.querySelector(targetSelector);
      if (modal) {
        e.preventDefault();
        e.stopPropagation();

        // Always ensure modal is in body
        if (modal.parentNode !== document.body) {
          document.body.appendChild(modal);
        }

        const buttonEl = openerEl.querySelector('button') || trigger;
        if (typeof modal.show === 'function') {
          modal.show(buttonEl);
        } else {
          modal.setAttribute('open', '');
          document.body.classList.add('overflow-hidden');
        }
        return;
      }
    }
  }

  // 2. Check if Close Button was clicked
  const closeBtn = e.target.closest('.quick-add-modal__toggle, [id^="ModalClose-"]');
  if (closeBtn) {
    e.preventDefault();
    const modal = closeBtn.closest('quick-add-modal') || document.querySelector('quick-add-modal[open]');
    if (modal) {
      if (typeof modal.hide === 'function') {
        modal.hide();
      } else {
        modal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
    }
    return;
  }

  // 3. Check if backdrop was clicked (outside modal content dialog)
  if (e.target.matches('quick-add-modal[open], .quick-add-modal[open]')) {
    e.preventDefault();
    const modal = e.target;
    if (typeof modal.hide === 'function') {
      modal.hide();
    } else {
      modal.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
  }
});

// ESC Key listener to close open modal
document.addEventListener('keyup', function(e) {
  if (e.key === 'Escape' || e.code === 'Escape') {
    const openModal = document.querySelector('quick-add-modal[open], .quick-add-modal[open]');
    if (openModal) {
      if (typeof openModal.hide === 'function') {
        openModal.hide();
      } else {
        openModal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
    }
  }
});

/* =========================================================================
   LUXURY QUICK OPTIONS HANDLERS
   ========================================================================= */
window.lqaSelectOption = function(btn, productId, optIndex, optValue) {
  const root = document.getElementById('lqa-root-' + productId);
  if (!root) return;

  // 1. Highlight clicked pill in this option group
  const group = btn.closest('.lqa-option-group');
  if (group) {
    group.querySelectorAll('.lqa-pill').forEach(p => p.classList.remove('is-active'));
    btn.classList.add('is-active');
  }

  // 2. Update active label text
  const currentVal = optValue || btn.getAttribute('data-option-value') || '';
  const labelVal = document.getElementById('lqa-active-val-' + productId + '-' + optIndex);
  if (labelVal) labelVal.textContent = currentVal;

  // 3. Collect all selected option values
  const allGroups = root.querySelectorAll('.lqa-option-group');
  const selectedOptions = [];
  allGroups.forEach(g => {
    const activePill = g.querySelector('.lqa-pill.is-active');
    if (activePill) selectedOptions.push(activePill.getAttribute('data-option-value'));
  });

  // 4. Find matching variant from registry
  const dataScript = document.getElementById('lqa-data-' + productId);
  if (!dataScript) return;
  let variants = [];
  try {
    variants = JSON.parse(dataScript.textContent);
  } catch(e) {
    console.error('Error parsing variants JSON:', e);
    return;
  }

  const matched = variants.find(v => {
    return v.options.every((opt, i) => opt === selectedOptions[i]);
  });

  if (!matched) return;

  // 5. Update preview image if available
  const imgEl = document.getElementById('lqa-img-' + productId);
  if (imgEl && matched.image) {
    imgEl.src = matched.image;
  }

  // 6. Update Price & Compare Price & Discount
  const priceEl = document.getElementById('lqa-price-' + productId);
  if (priceEl) priceEl.textContent = matched.price;

  const compareEl = document.getElementById('lqa-compare-' + productId);
  const discountEl = document.getElementById('lqa-discount-' + productId);
  const badgeEl = document.getElementById('lqa-badge-' + productId);

  if (matched.on_sale) {
    if (compareEl) {
      compareEl.textContent = matched.compare_at_price;
      compareEl.style.display = 'inline-block';
    }
    if (discountEl) {
      discountEl.textContent = matched.discount_pct;
      discountEl.style.display = 'inline-block';
    }
    if (badgeEl) badgeEl.style.display = 'inline-block';
  } else {
    if (compareEl) compareEl.style.display = 'none';
    if (discountEl) discountEl.style.display = 'none';
    if (badgeEl) badgeEl.style.display = 'none';
  }

  // 7. Update CTA button state & variant ID
  const atcBtn = document.getElementById('lqa-atc-' + productId);
  if (atcBtn) {
    atcBtn.setAttribute('data-variant-id', matched.id);
    const label = atcBtn.querySelector('.lqa-btn-label');
    if (matched.available) {
      atcBtn.removeAttribute('disabled');
      atcBtn.classList.remove('is-disabled');
      if (label) label.textContent = 'Add to cart';
    } else {
      atcBtn.setAttribute('disabled', 'disabled');
      atcBtn.classList.add('is-disabled');
      if (label) label.textContent = 'Sold out';
    }
  }
};

window.lqaChangeQty = function(productId, delta) {
  const input = document.getElementById('lqa-qty-' + productId);
  if (!input) return;
  let val = parseInt(input.value) || 1;
  val = Math.max(1, Math.min(30, val + delta));
  input.value = val;
};

window.lqaAddToCart = function(btn, productId) {
  const variantId = btn.getAttribute('data-variant-id');
  if (!variantId || btn.disabled) return;

  const qtyInput = document.getElementById('lqa-qty-' + productId);
  const qty = qtyInput ? (parseInt(qtyInput.value) || 1) : 1;

  btn.classList.add('is-loading');
  const label = btn.querySelector('.lqa-btn-label');
  const origText = label ? label.textContent : 'Add to cart';
  if (label) label.textContent = 'Adding...';

  const cartDrawer = document.querySelector('cart-drawer');
  const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function' 
    ? cartDrawer.getSectionsToRender().map(s => s.id) 
    : [];

  const cartAddUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';
  const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart.js';

  fetch(cartAddUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      id: variantId,
      quantity: qty,
      sections: sectionsToRender
    })
  })
  .then(res => {
    if (!res.ok) throw new Error('Cart error');
    return res.json();
  })
  .then(data => {
    btn.classList.remove('is-loading');
    btn.classList.add('is-added');
    if (label) label.textContent = 'Added ✓';

    // Update cart bubble counts across header
    fetch(cartUrl)
      .then(r => r.json())
      .then(cart => {
        document.querySelectorAll('.cart-count-bubble, [data-cart-count], .header__cart-count').forEach(el => {
          el.textContent = cart.item_count;
          el.classList.remove('hidden');
        });
      })
      .catch(e => console.error(e));

    // Render & open Cart Drawer if available
    if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
      cartDrawer.renderContents(data);
    } else if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

    // Close the QuickAdd modal after 1.2s
    setTimeout(() => {
      const modal = btn.closest('quick-add-modal') || document.querySelector('quick-add-modal[open]');
      if (modal && typeof modal.hide === 'function') {
        modal.hide();
      } else if (modal) {
        modal.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }
      btn.classList.remove('is-added');
      if (label) label.textContent = origText;
    }, 1200);
  })
  .catch(err => {
    console.error('Error adding to cart:', err);
    btn.classList.remove('is-loading');
    if (label) label.textContent = 'Error';
    setTimeout(() => {
      if (label) label.textContent = origText;
    }, 2000);
  });
};